import { useEffect, useState } from "react";

import { formatUsd } from "../../shared/money";
import type { CheckoutAttemptStatus } from "../../shared/types";
import { LoadingState } from "../components/LoadingState";
import { getCheckoutStatus } from "../lib/api";

const terminal = new Set(["captured", "stale", "cancelled", "expired", "failed", "refunded"]);

export function SuccessPage() {
  const attemptId = new URLSearchParams(window.location.search).get("attempt");
  const [status, setStatus] = useState<CheckoutAttemptStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attemptId) return;
    const controller = new AbortController();
    let timer = 0;
    const poll = async () => {
      try {
        const next = await getCheckoutStatus(attemptId, controller.signal);
        setStatus(next);
        if (!terminal.has(next.status)) timer = window.setTimeout(() => void poll(), 2_000);
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "Press status is unavailable.");
      }
    };
    void poll();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [attemptId]);

  if (!attemptId || error) {
    return (
      <main className="centered-page">
        <h1>THE BUTTON DIDN'T MOVE.</h1>
        <p>{error ?? "This confirmation link is incomplete."}</p>
        <a className="text-link" href="/">
          RETURN HOME
        </a>
      </main>
    );
  }
  if (!status || !terminal.has(status.status)) {
    return (
      <main className="centered-page">
        <LoadingState label="Waiting for durable payment confirmation" />
        <h1>DON'T CLOSE THIS YET.</h1>
        <p>Stripe has returned. The server is deciding whether this press won.</p>
      </main>
    );
  }

  if (status.status === "captured" && status.pressNumber) {
    return (
      <main className="centered-page success-page">
        <p className="section-kicker">YOU PRESSED IT.</p>
        <h1>You are Press #{status.pressNumber}.</h1>
        <p>
          {status.isCurrentHolder
            ? "You currently hold the featured homepage placement. Please don't let anyone replace you."
            : "Someone has already pressed after you, but your permanent history page is ready."}
        </p>
        <p>The next minimum is {formatUsd(status.currentPriceCents)}.</p>
        <a className="primary-link" href={`/press/${status.pressNumber}`}>
          OPEN YOUR PRESS PAGE
        </a>
      </main>
    );
  }

  if (status.status === "stale" || status.status === "cancelled") {
    return (
      <main className="centered-page stale-page">
        <p className="section-kicker">SOMEONE PRESSED FIRST.</p>
        <h1>Your payment was not captured.</h1>
        <p>Another valid payment reached the button before yours.</p>
        <p>Your bank may temporarily display an authorization while it is released.</p>
        <p>The current minimum is now {formatUsd(status.currentPriceCents)}.</p>
        <a className="primary-link" href="/">
          SEE THE CURRENT BUTTON
        </a>
      </main>
    );
  }

  return (
    <main className="centered-page">
      <h1>THE BUTTON DIDN'T MOVE.</h1>
      <p>No press was created and no successful capture was recorded.</p>
      <p>{status.failureReason}</p>
      <a className="primary-link" href="/">
        RETURN HOME
      </a>
    </main>
  );
}
